import { type Href, router } from 'expo-router';
import type { ReactNode } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { isWeb, type StackProps, XStack, YStack } from 'tamagui';

import {
    CONTENT_MAX_WIDTH,
    SCREEN_CONTAINER_HORIZONTAL_PADDING,
} from '../../constants';
import { BackButton, BackButtonIconType } from '../buttons/BackButton';
import { CloseButton } from '../buttons/CloseButton';
import { BodyLargeRegular, BodySmallRegular } from '../text/Typography';
import { SmoothieScrollView } from './Containers';

const SCREEN_SPACER_ELEMENT_MIN_WIDTH = 40;

interface ScreenContainerProps extends ScreenHeaderProps {
    containerProps?: StackProps;
    children: ReactNode;
    rightAction?: ReactNode | undefined;
    /**
     * Custom scroll container to use instead of SmoothieScrollView.
     *
     * USE SPARINGLY: Only use this when the default SmoothieScrollView is insufficient.
     *
     * Example use case: NestableScrollContainer from react-native-draggable-flatlist
     * is required in Income Split screens to enable drag-and-drop reordering of rules.
     */
    scrollContainerOverride?: (children: ReactNode) => ReactNode;
    maxWidthOverride?: number;
    numLinesForTitle?: number;
    useSafeAreaInsetBottom?: boolean;
    scrollEnabled?: boolean;
}

export const ScreenContainer = ({
    title,
    hasBackButton,
    onBack,
    onBeforeGoingBack,
    hasCloseButton,
    onClose,
    leftAction,
    subtitle,
    containerProps,
    onFallbackRoute,
    children,
    rightAction,
    scrollContainerOverride,
    maxWidthOverride,
    sideMinWidthOverride,
    numLinesForTitle,
    useSafeAreaInsetTop = true,
    useSafeAreaInsetBottom = true,
    scrollEnabled,
}: ScreenContainerProps) => {
    const { top, bottom } = useSafeAreaInsets();

    const shouldShowHeader =
        title != null ||
        !!hasBackButton ||
        !!hasCloseButton ||
        leftAction != null ||
        rightAction != null;

    const scrollContent =
        scrollContainerOverride != null ? (
            scrollContainerOverride(children)
        ) : (
            <SmoothieScrollView
                width="100%"
                paddingTop={12}
                paddingHorizontal={SCREEN_CONTAINER_HORIZONTAL_PADDING}
                scrollEnabled={scrollEnabled}
                containerStyle={{
                    maxWidth: maxWidthOverride ?? CONTENT_MAX_WIDTH,
                    marginHorizontal: 0,
                    marginTop: isWeb ? '0 !important' : 0,
                    paddingBottom: isWeb ? '24 !important' : 0,
                    width: '100%',
                    alignSelf: 'center',
                    flex: isWeb ? 1 : 0,
                }}
            >
                {children}
            </SmoothieScrollView>
        );

    return (
        <YStack
            flex={1}
            marginTop={
                isWeb || shouldShowHeader ? 0 : useSafeAreaInsetTop ? top : 0
            }
            marginBottom={isWeb ? 0 : useSafeAreaInsetBottom ? bottom : 0}
            {...containerProps}
        >
            {shouldShowHeader && (
                <ScreenHeader
                    title={title}
                    hasBackButton={hasBackButton}
                    onBack={onBack}
                    onBeforeGoingBack={onBeforeGoingBack}
                    hasCloseButton={hasCloseButton}
                    onClose={onClose}
                    leftAction={leftAction}
                    subtitle={subtitle}
                    rightAction={rightAction}
                    onFallbackRoute={onFallbackRoute}
                    maxWidthOverride={maxWidthOverride}
                    sideMinWidthOverride={sideMinWidthOverride}
                    numLinesForTitle={numLinesForTitle}
                    useSafeAreaInsetTop={useSafeAreaInsetTop}
                />
            )}
            {scrollContent}
        </YStack>
    );
};

interface ScreenHeaderProps {
    title?: string;
    subtitle?: string | null;
    hasBackButton?: boolean;
    onBack?: () => void;
    /**
     * Runs when the back button is used without a custom `onBack` handler, before
     * `router.back()` or `router.replace(fallbackRoute)`.
     */
    onBeforeGoingBack?: () => void;
    hasCloseButton?: boolean;
    onClose?: () => void;
    leftAction?: ReactNode;
    onFallbackRoute?: Href;
    rightAction?: ReactNode | undefined;
    maxWidthOverride?: number;
    sideMinWidthOverride?: number;
    numLinesForTitle?: number;
    useSafeAreaInsetTop?: boolean;
}

export const ScreenHeader = ({
    title,
    hasBackButton,
    onBack,
    onBeforeGoingBack,
    hasCloseButton,
    onClose,
    leftAction,
    subtitle,
    onFallbackRoute,
    rightAction,
    maxWidthOverride,
    sideMinWidthOverride,
    numLinesForTitle,
    useSafeAreaInsetTop = true,
}: ScreenHeaderProps) => {
    const { top } = useSafeAreaInsets();
    const sideMinWidth =
        sideMinWidthOverride ?? SCREEN_SPACER_ELEMENT_MIN_WIDTH;

    return (
        <XStack
            width="100%"
            paddingTop={isWeb ? 0 : useSafeAreaInsetTop ? top : 0}
            paddingHorizontal={SCREEN_CONTAINER_HORIZONTAL_PADDING}
            justifyContent="center"
            zIndex={1}
            backgroundColor="$white"
        >
            <XStack
                width="100%"
                maxWidth={maxWidthOverride ?? CONTENT_MAX_WIDTH}
                paddingTop={20}
                paddingBottom={12}
                alignItems="center"
                justifyContent="space-between"
            >
                <XStack
                    minWidth={sideMinWidth} // MinWidth allows for the element to still be used as a spacer to achieve a horizontally centered title, even when the nested button is not rendered.
                    alignItems="center"
                    justifyContent="flex-start"
                >
                    {leftAction}
                    {hasBackButton && (
                        <BackButton
                            background="gray"
                            fallbackRoute={onFallbackRoute}
                            iconType={BackButtonIconType.ARROW_LEFT}
                            customBackFn={onBack}
                            onBeforeGoingBack={onBeforeGoingBack}
                        />
                    )}
                </XStack>
                <YStack flex={1} alignItems="center">
                    <BodyLargeRegular
                        numberOfLines={numLinesForTitle}
                        textAlign="center"
                    >
                        {title}
                    </BodyLargeRegular>
                    {subtitle != null && (
                        <BodySmallRegular color="$gray1000" textAlign="center">
                            {subtitle}
                        </BodySmallRegular>
                    )}
                </YStack>
                <XStack
                    minWidth={sideMinWidth} // MinWidth allows for the element to still be used as a spacer to achieve a horizontally centered title, even when the nested button is not rendered.
                    alignItems="center"
                    justifyContent="flex-end"
                >
                    {rightAction}
                    {hasCloseButton && (
                        <CloseButton
                            background="gray"
                            onPress={
                                onClose ??
                                (() => {
                                    if (router.canGoBack()) {
                                        router.back();
                                    } else {
                                        router.navigate('/');
                                    }
                                })
                            }
                        />
                    )}
                </XStack>
            </XStack>
        </XStack>
    );
};
